import { createSlice } from '@reduxjs/toolkit'

import api, { createWrappedAsyncThunk } from 'src/api'

export const initialState = {
  businessSettings: {
    name: '',
    phoneNumber: null,
    primaryContactName: '',
    primaryContactEmail: '',
    registeredAddress: {
      name: '',
      line1: '',
      line2: null,
      city: '',
      county: '',
      postcode: '',
    },
  },
  purchasingEnvironments: [],
  roles: [],
  permissions: [],
  users: [],
}

const SLICE_NAME = 'admin'

export const fetchBusinessSettings = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchBusinessSettings`,
  () => api.get('/buyer/settings/')
)

export const updateBusinessSettings = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateBusinessSettings`,
  arg => api.patch('/buyer/settings/', { body: arg })
)

export const addPurchasingEnvironments = createWrappedAsyncThunk(
  `${SLICE_NAME}/addPurchasingEnvironments`,
  arg => api.post('/buyer/environments/', { body: arg })
)

export const fetchPurchasingEnvironments = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchPurchasingEnvironments`,
  arg => api.get('/buyer/environments/')
)

export const updatePurchasingEnvironments = createWrappedAsyncThunk(
  `${SLICE_NAME}/updatePurchasingEnvironments`,
  arg => api.patch(`/buyer/environments/${arg.id}/`, { body: arg })
)

export const deletePurchasingEnvironments = createWrappedAsyncThunk(
  `${SLICE_NAME}/deletePurchasingEnvironments`,
  arg => api.del(`/buyer/environments/${arg}/`)
)

export const fetchRoles = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchRoles`,
  args => api.get('/buyer/roles/')
)

export const addRoles = createWrappedAsyncThunk(
  `${SLICE_NAME}/addRoles`,
  args => api.post('/buyer/roles/', { body: args })
)

export const updateRoles = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateRoles`,
  args => api.patch(`/buyer/roles/${args.id}/`, { body: args })
)

export const deleteRoles = createWrappedAsyncThunk(
  `${SLICE_NAME}/deleteRoles`,
  arg => api.del(`/buyer/roles/${arg}/`)
)
export const fetchPermissions = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchPermissions`,
  args => api.get('/buyer/permissions/')
)

export const fetchUsers = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchUsers`,
  args => api.get('/buyer/useradmin/')
)

export const addUsers = createWrappedAsyncThunk(
  `${SLICE_NAME}/addUsers`,
  args => api.post('/buyer/useradmin/', { body: args })
)

export const updateUsers = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateUsers`,
  args => api.patch(`/buyer/useradmin/${args.id}/`, { body: args })
)

export const deleteUsers = createWrappedAsyncThunk(
  `${SLICE_NAME}/deleteUsers`,
  args => api.del(`/buyer/useradmin/${args}/delete/`)
)

export const restoreUsers = createWrappedAsyncThunk(
  `${SLICE_NAME}/restoreUsers`,
  args => api.post(`/buyer/useradmin/${args}/restore/`)
)

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: {
    [fetchBusinessSettings.fulfilled]: (state, { payload }) => {
      state.businessSettings = payload
      return state
    },
    [updateBusinessSettings.fulfilled]: (state, { payload }) => {
      state.businessSettings = payload
      return state
    },
    [fetchPurchasingEnvironments.fulfilled]: (state, { payload }) => {
      state.purchasingEnvironments = payload
      return state
    },
    [addPurchasingEnvironments.fulfilled]: (state, { payload }) => {
      state.purchasingEnvironments.results.push(payload)
      return state
    },
    [updatePurchasingEnvironments.fulfilled]: (state, { meta, payload }) => {
      const { arg } = meta
      const index = state.purchasingEnvironments.findIndex(
        env => env.id === arg.id
      )
      state.purchasingEnvironments[index] = payload
      return state
    },
    [deletePurchasingEnvironments.fulfilled]: (state, action) => {
      let id = action.meta.arg
      state.purchasingEnvironments.results =
        state.purchasingEnvironments.results.filter(env => env.id !== id)
      return state
    },
    [fetchRoles.fulfilled]: (state, { payload }) => {
      state.roles = payload
      return state
    },
    [addRoles.fulfilled]: (state, { payload }) => {
      state.roles.push(payload)
      return state
    },
    [updateRoles.fulfilled]: (state, { meta, payload }) => {
      const { arg } = meta
      const index = state.roles.findIndex(r => r.id === arg.id)
      state.roles[index] = payload
      return state
    },
    [deleteRoles.fulfilled]: (state, action) => {
      let id = action.meta.arg
      state.roles = state.roles.filter(role => role.id !== id)
      return state
    },
    [fetchPermissions.fulfilled]: (state, { payload }) => {
      state.permissions = payload
      return state
    },
    [fetchUsers.fulfilled]: (state, { payload }) => {
      state.users = payload
      return state
    },
    [addUsers.fulfilled]: (state, { payload }) => {
      state.users.push(payload)
      return state
    },
    [updateUsers.fulfilled]: (state, { meta, payload }) => {
      const { arg } = meta
      const index = state.users.findIndex(u => u.id === arg.id)
      state.users[index] = payload
      return state
    },
    [deleteUsers.fulfilled]: (state, { meta, payload }) => {
      const { arg } = meta
      const index = state.users.findIndex(u => u.id === arg)
      state.users[index] = payload
      return state
    },
    [restoreUsers.fulfilled]: (state, { meta, payload }) => {
      const { arg } = meta
      const index = state.users.findIndex(u => u.id === arg)
      state.users[index] = payload
      return state
    },
  },
})

export default slice.reducer
